import { Dialog, DialogFooter, PrimaryButton, Text } from "@fluentui/react";
import { useState } from "react";
import { PanelMode, PanelState } from "../types";
import { initialEditorValue } from "../helpers";
import { ICredentialInfo } from "types/stac";
import axios, { AxiosError } from "axios";
import { INMA_URL, API_VERSION } from "utils/constants";
import { useMutation, useQueryClient } from "react-query";
import { QUERY_KEYS } from "utils/requests";
import { ConfirmDialog } from "../common/Confirm";

export const useEditorPanelState = (initialState: PanelState) => {
  const [panelState, setState] = useState<PanelState>(initialState);

  const setPanelState = (newState: PanelState) => {
    setState(prevState => ({ ...prevState, ...newState }));
  };

  const handleCreateOpen = (collectionId?: string) => {
    setPanelState({
      isOpen: true,
      mode: "create",
      initialValue: initialEditorValue,
      collectionId: collectionId,
    });
  };

  const handlePanelClose = () => {
    setPanelState({ isOpen: false });
  };

  const handleEditOpen = (credentialInfo: any) => {
    setPanelState({
      isOpen: true,
      mode: "update",
      initialValue: credentialInfo,
    });
  };

  return {
    panelState,
    handleCreateOpen,
    handleEditOpen,
    handlePanelClose,
  };
};

export const useUpsertCredential = (
  credentialId: string,
  currentPage: number,
  pageSize: number,
  mode?: PanelMode
) => {
  const queryClient = useQueryClient();
  const [, setIsError] = useState(false);
  const [, setError] = useState<AxiosError | undefined>(undefined);
  const fn = mode === "create" ? createCredential : updateCredential;

  return useMutation(fn, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.credentialsDetail, credentialId]);
      queryClient.invalidateQueries([QUERY_KEYS.credentials, currentPage, pageSize]);
    },
    onError: (error: AxiosError) => {
      console.log("Mutation Error ", error);
      // Handle the error here
      setError(error);
      setIsError(true);
    },
  });
};

export const useDeleteCredential = (
  credentialId: string,
  currentPage: number,
  pageSize: number
) => {
  const queryClient = useQueryClient();
  const [isError, setIsError] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [error, setError] = useState<AxiosError | undefined>(undefined);

  const mutationDelete = useMutation(deleteCredential(credentialId), {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.credentialsDetail, credentialId]);
      queryClient.invalidateQueries([QUERY_KEYS.credentials, currentPage, pageSize]);
    },
    onError: (error: AxiosError) => {
      console.error("An error occurred while deleting the credential:", error);
      // Handle the error here
      setError(error);
      setIsError(true);
    },
  });

  const handleConfirmDelete = () => {
    setShowDeleteDialog(false);
    mutationDelete.mutate();
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
  };

  const handleErrorDialogDismiss = () => {
    setIsError(false);
  };

  const errorDialog = (
    <Dialog
      hidden={false}
      onDismiss={handleErrorDialogDismiss}
      maxWidth={500}
      modalProps={{
        isBlocking: true,
        styles: { main: { background: "#ffe6e6" } },
      }}
    >
      <Text block>
        An error occurred while deleting the credential: <br />
        <strong>`{credentialId}`</strong> <br />
        <br />
        {(error?.response?.data as { title?: string })?.title ? (
          <Text block>
            {(error?.response?.data as { title?: string })?.title} <br />
            {(error?.response?.data as { detail?: string })?.detail}"{" "}
          </Text>
        ) : null}
        <br />
        Error - {error?.code} <br />
        Message - {error?.message}
        <br />
        Stack - {JSON.stringify(error?.response?.data)}
      </Text>
      <DialogFooter>
        <PrimaryButton onClick={handleErrorDialogDismiss} text="Dismiss" />
      </DialogFooter>
    </Dialog>
  );

  const deleteDialog = (
    <ConfirmDialog
      title="Delete Credential"
      onCancel={handleCancelDelete}
      onConfirm={handleConfirmDelete}
    >
      <Text block>
        Are you sure you want to delete the selected credential{" "}
        <strong>`{credentialId}`</strong> ?
      </Text>
    </ConfirmDialog>
  );

  return {
    mutationDelete,
    showDeleteDialog,
    setShowDeleteDialog,
    deleteDialog,
    isError,
    errorDialog,
  };
};

const createCredential = async (credentialInput: ICredentialInfo) => {
  let data = {
    kind: credentialInput.sourceType,
    connectionInfo: credentialInput.connectionInfo,
  };
  const resp = await axios.post(
    `${INMA_URL}/ingestion-sources?api-version=${API_VERSION}`,
    data
  );
  return resp.data;
};

const updateCredential = async (credentialInput: ICredentialInfo) => {
  let data = {
    kind: credentialInput.sourceType,
    id: credentialInput.id,
    connectionInfo: credentialInput.connectionInfo,
  };
  const resp = await axios.put(
    `${INMA_URL}/ingestion-sources/${credentialInput.id}?api-version=${API_VERSION}`,
    data
  );
  return resp.data;
};

const deleteCredential = (credentialId: string) => {
  return async () => {
    const resp = await axios.delete(
      `${INMA_URL}/ingestion-sources/${credentialId}?api-version=${API_VERSION}`
    );
    //TODO Capture error
    return resp.data;
  };
};
